import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { BusinessContextService } from '../services/multi-tenant/business-context.service';

/**
 * Subscription Guard
 * Blocks access for suspended businesses, redirects to /suspended.
 * Cancelled businesses get a grace period until trialEndsAt or nextBillingDate.
 * Must run AFTER businessUserGuard (needs business context established).
 * Allows: active, trial, past_due, cancelled (within grace period)
 */
export const subscriptionGuard: CanActivateFn = async () => {
  const businessContext = inject(BusinessContextService);
  const router = inject(Router);

  const business = await businessContext.getCurrentBusiness();

  if (!business?.subscription?.status) {
    // No subscription data — allow access (defensive)
    return true;
  }

  const status = business.subscription.status;

  if (status === 'suspended') {
    router.navigate(['/suspended']);
    return false;
  }

  if (status === 'cancelled') {
    const now = new Date();
    const sub = business.subscription;

    // Check grace period: allow access until the end of the paid/trial period
    // Firestore returns Timestamp objects — use .toDate() if available
    const trialEnd = toDate(sub.trialEndsAt);
    const billingEnd = toDate(sub.nextBillingDate);
    const gracePeriodEnd = trialEnd || billingEnd;

    if (gracePeriodEnd && now < gracePeriodEnd) {
      return true; // Still within paid/trial period
    }

    router.navigate(['/suspended']);
    return false;
  }

  return true;
};

/** Convert Firestore Timestamp, Date, or date string to a JS Date. */
function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  // Firestore Timestamp objects have a toDate() method
  if (typeof (value as any).toDate === 'function') return (value as any).toDate();
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return null;
}
